import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Settings, X } from "lucide-react";

// Neuro-aesthetic color system
const COLORS = {
  warmStone: "#faf7f2",
  oliveSage: "#a07830",
  oliveSageHover: "#8a6628",
  mineralTaupe: "rgba(28,21,16,0.18)",
  warmGold: "#a07830",
  warmGoldHover: "#8a6628",
  inkGrey: "#1c1510",
  inkNavy: "#1c1510",
};

// Cookie categories for granular consent
const COOKIE_CATEGORIES = {
  essential: {
    name: "Essential Cookies",
    description: "Required for the website to function. Cannot be disabled.",
    required: true,
  },
  analytics: {
    name: "Analytics Cookies",
    description: "Help us understand how visitors interact with our website.",
    required: false,
  },
  marketing: {
    name: "Marketing Cookies",
    description: "Used to deliver relevant advertisements.",
    required: false,
  },
};

export default function ConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setIsVisible(true);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        if (savedPrefs.preferences) {
          setPreferences(savedPrefs.preferences);
        }
      } catch (e) {
        // Legacy consent format, show banner again
        setIsVisible(true);
      }
    }
  }, []);

  const saveConsent = (type, prefs) => {
    const consentData = {
      type,
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: "2.0", // GDPR/CCPA/Privacy Act compliant version
    };
    localStorage.setItem("cookie-consent", JSON.stringify(consentData));
    
    // Disable/enable analytics based on preference
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
    
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent("all", allAccepted);
  };

  const handleAcceptEssentialOnly = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    setPreferences(essentialOnly);
    saveConsent("essential", essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent("custom", preferences);
  };

  const togglePreference = (category) => {
    if (category === "essential") return; // Cannot disable essential
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  return (
    <div
      data-testid="consent-banner"
      className="fixed left-0 right-0 z-50 shadow-lg bottom-0 md:bottom-0"
      style={{ 
        backgroundColor: COLORS.warmStone,
        borderTop: `1px solid ${COLORS.mineralTaupe}`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {/* Main Banner */}
      {!showPreferences && (
        <div className="p-6 md:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
              <div className="flex-1">
                <h3 className="font-serif text-lg mb-2" style={{ color: COLORS.inkGrey }}>
                  Your Privacy Matters
                </h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: `${COLORS.inkGrey}99` }}>
                  We use cookies to enhance your experience. Under GDPR (EU), CCPA (USA), and the Australian Privacy Act, 
                  you have the right to control which cookies are used. Essential cookies are required for basic functionality.
                </p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <Link
                    to="/legal/privacy-policy"
                    className="text-xs hover:underline"
                    style={{ color: COLORS.oliveSage }}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/legal/cookies-policy"
                    className="text-xs hover:underline"
                    style={{ color: COLORS.oliveSage }}
                  >
                    Cookies Policy
                  </Link>
                  <Link
                    to="/legal/terms-and-conditions"
                    className="text-xs hover:underline"
                    style={{ color: COLORS.oliveSage }}
                  >
                    Terms & Conditions
                  </Link>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button
                  data-testid="consent-preferences-btn"
                  onClick={() => setShowPreferences(true)}
                  variant="outline"
                  className="rounded-full px-5 py-2 text-sm flex items-center gap-2"
                  style={{ 
                    borderColor: COLORS.mineralTaupe,
                    color: COLORS.inkGrey
                  }}
                >
                  <Settings size={14} />
                  Customize
                </Button>
                <Button
                  data-testid="consent-essential-btn"
                  onClick={handleAcceptEssentialOnly}
                  variant="outline"
                  className="rounded-full px-5 py-2 text-sm"
                  style={{ 
                    borderColor: COLORS.mineralTaupe,
                    color: COLORS.inkGrey
                  }}
                >
                  Essential Only
                </Button>
                <Button
                  data-testid="consent-accept-btn"
                  onClick={handleAcceptAll}
                  className="rounded-full px-6 py-2 text-sm"
                  style={{ 
                    backgroundColor: COLORS.inkNavy,
                    color: "#fff"
                  }}
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Panel */}
      {showPreferences && (
        <div className="p-6 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-lg" style={{ color: COLORS.inkGrey }}>
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-2 rounded-full hover:bg-black/5 transition-colors"
                aria-label="Close preferences"
              >
                <X size={20} style={{ color: COLORS.inkGrey }} />
              </button>
            </div>
            
            <p className="text-sm mb-6" style={{ color: `${COLORS.inkGrey}99` }}>
              Manage your cookie preferences below. Your choices comply with GDPR (EU), CCPA (California, USA), 
              and the Australian Privacy Act. You can change these settings at any time.
            </p>

            <div className="space-y-4 mb-6">
              {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => (
                <div 
                  key={key}
                  className="flex items-start justify-between p-4 rounded-sm"
                  style={{ backgroundColor: COLORS.warmStoneHover }}
                >
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-medium mb-1" style={{ color: COLORS.inkGrey }}>
                      {category.name}
                      {category.required && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${COLORS.oliveSage}20`, color: COLORS.oliveSage }}>
                          Required
                        </span>
                      )}
                    </p>
                    <p className="text-xs" style={{ color: `${COLORS.inkGrey}80` }}>
                      {category.description}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[key]}
                      onChange={() => togglePreference(key)}
                      disabled={category.required}
                      className="sr-only peer"
                    />
                    <div 
                      className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${category.required ? 'opacity-60 cursor-not-allowed' : ''}`}
                      style={{ 
                        backgroundColor: preferences[key] ? COLORS.oliveSage : COLORS.mineralTaupe 
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                onClick={handleAcceptEssentialOnly}
                variant="outline"
                className="rounded-full px-5 py-2 text-sm"
                style={{ 
                  borderColor: COLORS.mineralTaupe,
                  color: COLORS.inkGrey
                }}
              >
                Essential Only
              </Button>
              <Button
                data-testid="consent-save-preferences-btn"
                onClick={handleSavePreferences}
                className="rounded-full px-6 py-2 text-sm"
                style={{ 
                  backgroundColor: COLORS.inkNavy,
                  color: "#fff"
                }}
              >
                Save Preferences
              </Button>
            </div>

            <p className="text-xs mt-4 text-center" style={{ color: `${COLORS.inkGrey}60` }}>
              For more information about your privacy rights, please review our{" "}
              <Link to="/legal/privacy-policy" className="underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
